import { Link, useSearchParams } from 'react-router-dom';
import { partner } from '../../api/partner';
import type { CaseItem } from '../../api/types';
import { canWriteCases, useSession } from '../../app/session';
import { ErrorBlock } from '../../components/ErrorBlock';
import { ChevronIcon } from '../../components/Icons';
import { Loading } from '../../components/Loading';
import { PageHeader } from '../../components/PageHeader';
import { useT } from '../../i18n';
import { useFormat } from '../../lib/format';
import { useAsync, useOnVisible } from '../../lib/useAsync';

export function caseRoute(c: Pick<CaseItem, 'origin_country' | 'dest_country'>, country: (c: string | null) => string): string {
  const a = country(c.origin_country);
  const b = country(c.dest_country);
  return a || b ? `${a || '…'} → ${b || '…'}` : '';
}

/** Own cases of the partner tenant (GET /cases — RLS returns only this company's cases). */
export function CasesPage() {
  const t = useT();
  const me = useSession((s) => s.me);
  const [sp, setSp] = useSearchParams();
  const closed = sp.get('tab') === 'closed';
  const q = useAsync(() => partner.cases(), []);
  useOnVisible(() => void q.reload());
  const items = (q.data?.items ?? []).filter((c) => !!c.closed_at === closed);
  return (
    <>
      <PageHeader
        title={t('cases.title')}
        right={
          canWriteCases(me) ? (
            <Link to="/cases/new" className="btn-primary mt-1">
              + {t('cases.new')}
            </Link>
          ) : null
        }
      />
      <div role="tablist" className="mb-4 grid grid-cols-2 gap-2">
        {(['open', 'closed'] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={closed === (k === 'closed')}
            onClick={() => setSp(k === 'closed' ? { tab: 'closed' } : {}, { replace: true })}
            className={`min-h-tap rounded-xl border text-[15px] font-medium ${closed === (k === 'closed') ? 'border-fg bg-acc text-acc-fg' : 'border-line bg-card'}`}
          >
            {t(k === 'open' ? 'cases.open' : 'cases.closed')}
          </button>
        ))}
      </div>
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorBlock error={q.error} onRetry={() => void q.reload()} /> : null}
      {q.data && !items.length ? <p className="text-lab">{t('cases.empty')}</p> : null}
      <ul className="space-y-3">
        {items.map((c) => (
          <li key={c.id}>
            <CaseCardLink c={c} />
          </li>
        ))}
      </ul>
    </>
  );
}

export function CaseCardLink({ c }: { c: CaseItem }) {
  const f = useFormat();
  const route = caseRoute(c, f.country);
  return (
    <Link to={`/cases/${c.id}`} className="card flex items-center gap-3 active:bg-hov">
      <div className="min-w-0 flex-1">
        <span className="text-[17px] font-semibold">{c.number}</span>
        {c.deceased_name ? <p className="text-[16px]">{c.deceased_name}</p> : c.title ? <p className="text-[16px]">{c.title}</p> : null}
        {route ? <p className="text-[15px] text-txt">{route}</p> : null}
        <p className="text-[14px] text-lab">{f.date(c.updated_at, false)}</p>
      </div>
      <ChevronIcon />
    </Link>
  );
}
